import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Flex,
  Image,
  Input,
  Modal,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  DeleteOutlined,
  FileImageOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { axiosInstance } from "@/utilities";
import { API_URL_PIM_PRODUCTS } from "@/services/XCONS_ENV";
import type { CatalogHit, CatalogSearchResponse, CategoryNode } from "./types";
import { ESTADOS, estadoDelHit } from "./estados";
import { TARJETA_CON_ENCABEZADO } from "./HistorialDelCatalogo";
import { PaginationTotal } from "@/components/pagination-total";
import { FILAS_POR_PAGINA, OPCIONES_POR_PAGINA } from "@/config/paginacion";

const { Text } = Typography;

/** El mismo default que Atributos y Conjuntos; se cambia desde el paginador. */
const POR_PAGINA = 50;
const RESULTADOS = 8;

/**
 * La tarjeta mide 500px (xcons-docs#449). Lo que sobra después del borde (2), el
 * encabezado (40, con el buscador adentro) y el paginado (62) es lo que ocupan las filas,
 * **siempre**: con pocas el paginado queda igual contra el fondo, sin un hueco abajo. Se aplica con `.tabla-alto-fijo`, porque
 * el alto de todas las tablas lo pisa `config.css` con `!important`.
 */
const ALTO_TARJETA = 500;
const ALTO_FILAS = ALTO_TARJETA - 2 - 40 - 62;

/**
 * La foto de un producto en una fila. Sólo se muestra una URL completa: un path suelto no
 * dice de qué bucket es, y adivinarlo daba el ícono roto.
 */
const FotoDeProducto: React.FC<{ src?: string | false | null }> = ({ src }) =>
  src && /^https?:\/\//.test(src) ? (
    <Image
      src={src}
      width={40}
      height={40}
      preview={false}
      style={{ objectFit: "contain", background: "#fafafa" }}
    />
  ) : (
    <Flex
      align="center"
      justify="center"
      style={{ width: 40, height: 40, background: "#fafafa", flexShrink: 0 }}
    >
      <FileImageOutlined style={{ fontSize: 18, color: "#bfbfbf" }} />
    </Flex>
  );

/** Una fila de la lista. Todo menos `sku` y `name` llega con xcons-docs#424. */
interface FilaDeProducto {
  sku: string;
  name: string | null;
  image?: string | null;
  state?: string;
  /** La categoría a la que está asignado directo; es la pedida si no viene de una hija. */
  directCategoryId?: number;
  directCategoryName?: string;
  pendingApproval?: boolean;
}

/** La respuesta paginada del contrato de xcons-docs#424. */
interface PaginaDeProductos {
  items: FilaDeProducto[];
  total: number;
}

type Resultado = {
  pending?: string[];
  failed?: Array<{ sku: string; code?: string; message?: string }>;
};

const mensajeDe = (e: unknown): string | undefined =>
  (e as { response?: { data?: { message?: string } } })?.response?.data
    ?.message;

/**
 * Los productos de una categoría del Store, abajo del árbol (xcons-docs#449), con lo que
 * pide xcons-docs#426: foto, nombre, SKU y estado, paginado, y sacar de a uno.
 *
 * **Agregar es un solo buscador, como el de Presupuestos**: los resultados del catálogo se
 * listan debajo mientras se escribe, y cada uno lleva "Agregar" a la derecha salvo que ya
 * esté en la categoría. Busca en el índice del catálogo, así que sólo ofrece productos
 * **publicados**.
 *
 * **Anda con las dos respuestas del API.** Hoy devuelve `{sku, name}[]` entero, sin paginar:
 * ahí se pagina en pantalla. Con xcons-docs#424 devuelve `{items, total}` ya paginado, con
 * foto, estado, la subcategoría de origen y si hay una aprobación pendiente, y la tabla lo
 * muestra sin tocar nada más.
 */
export const ProductosDeLaCategoria: React.FC<{
  storeCatalogUrl: string;
  categoria: CategoryNode;
  /** El árbol de XCONS: sacar un producto cambia el menú de todos los que lo comparten. */
  arbolCompartido: boolean;
  /** Sube con cada recarga del árbol: la lista la sigue. */
  recarga: number;
  /** Después de asignar o sacar: recarga el árbol, que trae los conteos nuevos. */
  onCambio: () => Promise<void>;
  /**
   * Agregar y sacar productos (xcons-docs#470): Editor for all en el Catálogo **y** Editor en
   * Categorías del store, como mínimo. Sin eso la lista se ve, sin el buscador de agregar ni
   * el tachito. El API exige lo mismo.
   */
  puedeEditar: boolean;
}> = ({ storeCatalogUrl, categoria, arbolCompartido, recarga, onCambio, puedeEditar }) => {
  const { t } = useTranslation();
  const [respuesta, setRespuesta] = useState<
    FilaDeProducto[] | PaginaDeProductos
  >([]);
  const [cargando, setCargando] = useState(false);
  const [page, setPage] = useState(1);
  const [tamPagina, setTamPagina] = useState(POR_PAGINA);
  const [texto, setTexto] = useState("");
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState<CatalogHit[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [errorDeBusqueda, setErrorDeBusqueda] = useState(false);
  /** Los SKUs del buscador que ya están en la categoría: a esos no se les ofrece "Agregar". */
  const [yaEstan, setYaEstan] = useState<Set<string>>(new Set());
  const [agregando, setAgregando] = useState<string | null>(null);
  /** Los resultados flotan sobre la tabla; un click afuera o Escape los cierra. */
  const [abierto, setAbierto] = useState(false);
  const campo = useRef<HTMLDivElement>(null);
  const lista = useRef<HTMLDivElement>(null);
  const cuerpo = useRef<HTMLDivElement>(null);
  /**
   * Dónde cae la lista, medido contra el buscador. El buscador está en el encabezado y la
   * lista no puede estar ahí: el título de una Card de antd corta lo que se sale
   * (`overflow: hidden`). Va arriba del cuerpo, alineada con el campo.
   */
  const [posicion, setPosicion] = useState({ left: 16, width: 0 });

  useEffect(() => {
    if (!abierto) return;
    const medir = () => {
      const c = campo.current?.getBoundingClientRect();
      const b = cuerpo.current?.getBoundingClientRect();
      if (c && b) setPosicion({ left: c.left - b.left, width: c.width });
    };
    medir();
    const alClickear = (e: MouseEvent) => {
      const dentro = [campo, lista].some((r) =>
        r.current?.contains(e.target as Node),
      );
      if (!dentro) setAbierto(false);
    };
    window.addEventListener("resize", medir);
    document.addEventListener("mousedown", alClickear);
    return () => {
      window.removeEventListener("resize", medir);
      document.removeEventListener("mousedown", alClickear);
    };
  }, [abierto]);

  const urlProductos = `${storeCatalogUrl}/categories/${categoria.categoryId}/products`;

  // Otra categoría arranca de cero: sin esto quedaban la búsqueda y la página de la anterior.
  useEffect(() => {
    setTexto("");
    setQ("");
    setPage(1);
    setRespuesta([]);
  }, [categoria.categoryId]);

  useEffect(() => {
    const id = setTimeout(() => setQ(texto.trim()), 300);
    return () => clearTimeout(id);
  }, [texto]);

  // Los resultados del buscador, y cuáles ya están en la categoría. Lo segundo se le pregunta
  // a la categoría con la misma búsqueda: con la lista paginada de xcons-docs#424, la página a
  // la vista no alcanza para saberlo.
  useEffect(() => {
    if (q.length < 2) {
      setResultados([]);
      setErrorDeBusqueda(false);
      return;
    }
    let vigente = true;
    setBuscando(true);
    setErrorDeBusqueda(false);
    Promise.all([
      axiosInstance.get<CatalogSearchResponse>(API_URL_PIM_PRODUCTS, {
        // `page` es 1-based, como en el listado.
        params: { search: q, hitsPerPage: RESULTADOS, page: 1 },
      }),
      axiosInstance
        .get<FilaDeProducto[] | PaginaDeProductos>(urlProductos, {
          params: { search: q, page: 1, pageSize: 100 },
        })
        .catch(() => ({ data: [] as FilaDeProducto[] })),
    ])
      .then(([catalogo, enLaCategoria]) => {
        if (!vigente) return;
        const filas = Array.isArray(enLaCategoria.data)
          ? enLaCategoria.data
          : enLaCategoria.data.items ?? [];
        setResultados(catalogo.data.hits ?? []);
        setYaEstan(new Set(filas.map((p) => p.sku)));
      })
      .catch(() => {
        if (!vigente) return;
        setResultados([]);
        setErrorDeBusqueda(true);
      })
      .finally(() => vigente && setBuscando(false));
    return () => {
      vigente = false;
    };
  }, [q, urlProductos, recarga]);

  useEffect(() => {
    let vigente = true;
    setCargando(true);
    axiosInstance
      .get<FilaDeProducto[] | PaginaDeProductos>(urlProductos, {
        params: { page, pageSize: tamPagina, includeChildren: "true" },
      })
      .then(({ data }) => vigente && setRespuesta(data ?? []))
      .catch(() => vigente && setRespuesta([]))
      .finally(() => vigente && setCargando(false));
    // Cambiar de categoría rápido no tiene que dejar a la vista la lista de la anterior.
    return () => {
      vigente = false;
    };
  }, [urlProductos, page, tamPagina, recarga]);

  /** Lo que muestra la tabla, venga paginado del API o entero. */
  const { filas, total } = useMemo(
    () =>
      Array.isArray(respuesta)
        ? {
            filas: respuesta.slice((page - 1) * tamPagina, page * tamPagina),
            total: respuesta.length,
          }
        : { filas: respuesta.items ?? [], total: respuesta.total ?? 0 },
    [respuesta, page, tamPagina],
  );

  /** Sólo se saca lo asignado directo: lo que viene de una hija se saca en la hija (D2.4). */
  const esDirecto = (p: FilaDeProducto) =>
    p.directCategoryId === undefined ||
    p.directCategoryId === categoria.categoryId;

  /**
   * Lo que devuelve asignar o sacar, además de lo que se aplicó directo (xcons-docs#440):
   * un producto **publicado** no cambia en el acto, queda como versión pendiente; y uno que no
   * se pudo tocar vuelve con su motivo.
   */
  const avisarPendientesYFallos = (data: Resultado) => {
    const pendientes = data.pending ?? [];
    const fallidos = data.failed ?? [];
    const lista = (skus: string[]) =>
      `${skus.slice(0, 5).join(", ")}${skus.length > 5 ? "…" : ""}`;
    if (pendientes.length) {
      message.info(
        `${pendientes.length} ${
          pendientes.length === 1
            ? t("storeCategories.pendingOne", "quedó pendiente de aprobación")
            : t(
                "storeCategories.pendingMany",
                "quedaron pendientes de aprobación",
              )
        } (${lista(pendientes)})`,
        6,
      );
    }
    if (fallidos.length) {
      message.warning(
        `${t("storeCategories.failed", "No se pudieron cambiar")} ${
          fallidos.length
        }: ${lista(
          fallidos.map((f) => (f.message ? `${f.sku} (${f.message})` : f.sku)),
        )}`,
        8,
      );
    }
  };

  const asignar = async (sku: string) => {
    const skus = [sku];
    setAgregando(sku);
    try {
      const { data } = await axiosInstance.post<
        Resultado & { assigned: number; notFound: string[] }
      >(urlProductos, { skus });
      // Si todo quedó pendiente, decir "asignados" sería mentir: el aviso de pendientes
      // es el único.
      if (data.assigned || !(data.pending?.length || data.failed?.length)) {
        message.success(
          `${data.assigned} ${t(
            "storeCategories.productsAssigned",
            "productos asignados",
          )}`,
        );
      }
      avisarPendientesYFallos(data);
      // Con el buscador no debería pasar, pero el índice puede ir atrás de la base.
      if (data.notFound?.length) {
        message.warning(
          `${t("storeCategories.notFound", "No se encontraron")} ${
            data.notFound.length
          }: ${data.notFound.slice(0, 5).join(", ")}${
            data.notFound.length > 5 ? "…" : ""
          }`,
        );
      }
      // Se esconde el botón en el acto; la recarga de abajo lo confirma.
      if (data.assigned || data.pending?.length) {
        setYaEstan((prev) => new Set(prev).add(sku));
      }
      await onCambio();
    } catch (e) {
      message.error(
        mensajeDe(e) ?? t("storeCategories.assignError", "No se pudo asignar"),
      );
    } finally {
      setAgregando(null);
    }
  };

  const sacar = (sku: string) =>
    Modal.confirm({
      title: `Sacar ${sku} de "${categoria.name}"`,
      // Primario y "Sí, …", como Rechazar (xcons-docs#466): se puede volver a asignar.
      okText: "Sí, sacar",
      cancelText: t("buttons.cancel", "Cancelar"),
      content: arbolCompartido
        ? "Esta categoría es del árbol de XCONS: el cambio se ve en el menú de todos los Stores que lo comparten."
        : undefined,
      onOk: async () => {
        try {
          const { data } = await axiosInstance.post<Resultado>(
            `${urlProductos}/unassign`,
            { skus: [sku] },
          );
          avisarPendientesYFallos(data ?? {});
          await onCambio();
        } catch (e) {
          // El 403 de "no podés editar este producto" trae su motivo: se muestra ése.
          message.error(
            mensajeDe(e) ??
              t("storeCategories.unassignError", "No se pudo desasignar"),
          );
        }
      },
    });

  const hayEstado = filas.some((p) => p.state || p.pendingApproval);

  return (
    <Card
      title={
        // El buscador va en el encabezado, al lado del título, como el del historial.
        <Flex align="center" gap={24} style={{ width: "100%" }}>
          <Text style={{ ...TARJETA_CON_ENCABEZADO.titulo, flexShrink: 0 }}>
            {`${t("storeCategories.productsOf", "Productos de")} "${
              categoria.name
            }"`}
          </Text>
          {puedeEditar && (
          <div
            ref={campo}
            style={{ flex: 1, minWidth: 0 }}
            onKeyDown={(e) => e.key === "Escape" && setAbierto(false)}
          >
            {/* Del alto de los botones, como el del árbol (xcons-docs#466). */}
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: "#999" }} />}
              suffix={buscando ? <Spin size="small" /> : null}
              placeholder="Buscá productos por nombre o SKU para agregarlos"
              value={texto}
              onChange={(e) => {
                setTexto(e.target.value);
                setAbierto(true);
              }}
              onFocus={() => setAbierto(true)}
            />
          </div>
          )}
        </Flex>
      }
      // Las filas de la tabla llegan hasta el borde de la tarjeta (xcons-docs#449): el
      // margen lo llevan los buscadores y el paginado, no el cuerpo.
      style={{ ...TARJETA_CON_ENCABEZADO.style, height: ALTO_TARJETA }}
      styles={{
        header: TARJETA_CON_ENCABEZADO.header,
        // Sin `overflow: hidden`: recortaba los resultados del buscador, que flotan encima.
        // El `paddingTop` de 1px: antd sube el cuerpo 1px sobre el encabezado y la tabla,
        // blanca, le tapaba el borde de abajo (xcons-docs#466).
        body: { padding: 0, paddingTop: 1 },
      }}
    >
      <div ref={cuerpo} style={{ position: "relative" }}>
        {/* Mientras vuelve una búsqueda nueva quedan los resultados anteriores: vaciarlos
            hacía saltar el desplegable con cada letra. */}
        {puedeEditar && abierto && q.length >= 2 && (resultados.length > 0 || !buscando) && (
          <div
            ref={lista}
            onKeyDown={(e) => e.key === "Escape" && setAbierto(false)}
            style={{
              position: "absolute",
              top: 4,
              left: posicion.left,
              width: posicion.width || undefined,
              right: posicion.width ? undefined : 16,
              minWidth: 480,
              zIndex: 1050,
              background: "#fff",
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              boxShadow:
                "0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)",
              maxHeight: 420,
              overflowY: "auto",
            }}
          >
            {resultados.length === 0 ? (
              <Text type="secondary" style={{ display: "block", padding: 12 }}>
                {errorDeBusqueda
                  ? "No se pudo buscar"
                  : `Ningún producto coincide con "${q}"`}
              </Text>
            ) : (
              resultados.map((h) => {
                const estado = ESTADOS[estadoDelHit(h)];
                return (
                  <Flex
                    key={h.sku}
                    align="center"
                    gap={12}
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #f5f5f5",
                    }}
                  >
                    <FotoDeProducto src={h.image} />
                    <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                      <Text strong ellipsis style={{ fontSize: 13 }}>
                        {h.name}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {h.sku}
                      </Text>
                    </Flex>
                    <Tag color={estado.color} style={{ ...estado.style, margin: 0 }}>
                      {estado.label}
                    </Tag>
                    <div
                      style={{ width: 150, textAlign: "right", flexShrink: 0 }}
                    >
                      {yaEstan.has(h.sku) ? (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Ya está en la categoría
                        </Text>
                      ) : (
                        <Button
                          size="small"
                          icon={<PlusOutlined />}
                          loading={agregando === h.sku}
                          disabled={!!agregando && agregando !== h.sku}
                          onClick={() => void asignar(h.sku)}
                        >
                          Agregar
                        </Button>
                      )}
                    </div>
                  </Flex>
                );
              })
            )}
          </div>
        )}
      </div>

      <Table<FilaDeProducto>
        rowKey="sku"
        size="small"
        loading={cargando}
        dataSource={filas}
        locale={{ emptyText: "Esta categoría todavía no tiene productos" }}
        className="tabla-alto-fijo"
        // Sin encabezado de columnas: cada fila ya dice qué es (xcons-docs#449).
        showHeader={false}
        style={{ "--alto-filas": `${ALTO_FILAS}px` } as React.CSSProperties}
        scroll={{ y: ALTO_FILAS }}
        // El mismo paginador que el resto de las tablas (xcons-docs#390): de tamaño normal
        // aunque la tabla sea `small`, con el total y el selector de cantidad.
        pagination={{
          size: "default",
          current: page,
          pageSize: tamPagina,
          total,
          showSizeChanger: true,
          pageSizeOptions: OPCIONES_POR_PAGINA,
          // En español, con las mismas claves que el listado del Catálogo.
          locale: {
            items_per_page: t("table.itemsPerPage"),
            prev_page: t("table.prevPage"),
            next_page: t("table.nextPage"),
            jump_to: t("table.jumpTo"),
            jump_to_confirm: t("table.jumpToConfirm"),
            page: t("table.page"),
            next_5: t("table.next5"),
            prev_5: t("table.prev5"),
          },
          onChange: (p, tam) => {
            // Cambiar la cantidad vuelve a la primera página: la actual puede no existir.
            setPage(tam !== tamPagina ? 1 : p);
            setTamPagina(tam);
          },
          showTotal: (n) => (
            <PaginationTotal total={n} entityName="productos" />
          ),
        }}
        columns={[
          {
            key: "foto",
            width: 56,
            render: (_, p) => <FotoDeProducto src={p.image} />,
          },
          {
            title: "Producto",
            key: "producto",
            render: (_, p) => (
              <Flex vertical>
                <Text>{p.name ?? "—"}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {p.sku}
                </Text>
              </Flex>
            ),
          },
          ...(hayEstado
            ? [
                {
                  title: "Estado",
                  key: "estado",
                  width: 200,
                  render: (_: unknown, p: FilaDeProducto) => (
                    <Space size={4} wrap>
                      {p.state && ESTADOS[p.state] && (
                        <Tag
                          color={ESTADOS[p.state].color}
                          style={ESTADOS[p.state].style}
                        >
                          {ESTADOS[p.state].label}
                        </Tag>
                      )}
                      {p.pendingApproval && (
                        <Tag color="gold">Pendiente de aprobación</Tag>
                      )}
                    </Space>
                  ),
                },
              ]
            : []),
          // Sin columna "Viene de" (xcons-docs#466): en casi todas las filas decía "Esta
          // categoría" y no sumaba. Lo que viene de una subcategoría se sigue sacando desde
          // ahí (D2.4), por eso no lleva el botón.
          {
            key: "sacar",
            width: 48,
            render: (_: unknown, p: FilaDeProducto) =>
              puedeEditar && esDirecto(p) ? (
                <Tooltip title="Sacar de esta categoría">
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined style={{ color: "#000" }} />}
                    onClick={() => sacar(p.sku)}
                  />
                </Tooltip>
              ) : null,
          },
        ]}
      />
    </Card>
  );
};
